// LeadTimePricingChart.jsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Box } from "@mui/material";
import { useAuth } from "../../sdk";

const colorPalette = ["#E74C3C", "#3498DB", "#2ECC71", "#F39C12", "#9B59B6"];
const getPriceValue = (price) => {
  if (price === null || price === undefined || Number.isNaN(price)) {
    return 0;
  }
  if (price <= 0) {
    return 0;
  }
  return price;
};

const getPriceLabel = (rawPrice, fallbackValue) => {
  if (rawPrice === 0) return "Sold Out";
  if (rawPrice === null || rawPrice === undefined || rawPrice < 0) return "N/A";
  return `₹${fallbackValue}`;
};

const LeadTimePricingChart = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [opacity, setOpacity] = useState({});
  const { currentHotel } = useAuth();

  const transformDataForChart = useCallback((apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData
      .filter((item) => item.leadTime >= 0)
      .map((item) => {
        const formattedDate = new Date(item.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });

        const chartItem = {
          date: formattedDate,
          leadTime: item.leadTime,
          occupancy:
            item.occupancyPercentage > 0 ? item.occupancyPercentage : 0,
          currentPrice: getPriceValue(item.currentPrice),
          currentPriceRaw: item.currentPrice,
          systemPrice: getPriceValue(item.recommendedPrice),
          systemPriceRaw: item.recommendedPrice,
        };

        if (item.compSet && Array.isArray(item.compSet)) {
          item.compSet.forEach((comp) => {
            const compName = comp.competitorName;
            chartItem[compName] = getPriceValue(comp.price);
            chartItem[`${compName}Raw`] = comp.price;
          });
        }

        return chartItem;
      });
  }, []);

  const chartData = useMemo(() => transformDataForChart(data), [
    data,
    transformDataForChart,
  ]);

  const competitorNames = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const firstValidItem = data.find(
      (item) => item.compSet && item.compSet.length > 0,
    );
    if (!firstValidItem) return [];

    return firstValidItem.compSet.map((comp) => comp.competitorName);
  }, [data]);

  useEffect(() => {
    const initialOpacity = {
      occupancy: 1,
      systemPrice: 1,
      currentPrice: 1,
    };

    competitorNames.forEach((name) => {
      initialOpacity[name] = 1;
    });

    setOpacity(initialOpacity);
  }, [competitorNames]);

  const maxPrice = useMemo(() => {
    if (!chartData || chartData.length === 0) return 20000;

    let maxPriceValue = 0;
    chartData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (
          key === "date" ||
          key === "leadTime" ||
          key === "occupancy" ||
          key.endsWith("Raw")
        ) {
          return;
        }
        const value = item[key];
        if (value !== null && value !== undefined && value > maxPriceValue) {
          maxPriceValue = value;
        }
      });
    });

    return Math.ceil(maxPriceValue * 1.1);
  }, [chartData]);

  const handleLegendClick = useCallback((o) => {
    const { dataKey } = o;
    setOpacity((prevOpacity) => ({
      ...prevOpacity,
      [dataKey]: prevOpacity[dataKey] === 1 ? 0 : 1,
    }));
  }, []);

  const handleColorChange = useCallback((entry, index) => {
    setActiveIndex(index);
  }, []);

  const handleColorReset = useCallback(() => {
    setActiveIndex(-1);
  }, []);

  if (!chartData || chartData.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "600px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Roboto",
        }}
      >
        No data available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "600px",
        padding: "20px",
        backgroundColor: "#fff",
        boxShadow: "1px 4px 10px rgba(48, 111, 188, 0.2)",
      }}
    >
      <Box
        sx={{
          fontSize: "18px",
          fontWeight: "500",
          marginBottom: "20px",
          fontFamily: "Roboto",
        }}
      >
        Occupancy% vs Pricing (Lead Time View)
      </Box>
      <ResponsiveContainer width="100%" height="90%">
        <ComposedChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid stroke="#FFFFFF" />
          <XAxis
            dataKey="leadTime"
            style={{ fontFamily: "Roboto", fontSize: "13px" }}
            interval={0}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            yAxisId="left"
            orientation="left"
            stroke="#777777"
            type="number"
            domain={[0, 100]}
            style={{ fontFamily: "Roboto", fontSize: "13px" }}
            tickFormatter={(tick) => `${tick}%`}
            label={{
              value: "Occupancy%",
              angle: -90,
              position: "insideLeft",
              fontFamily: "Roboto",
              fontSize: "14px",
              color: "#777777",
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#777777"
            style={{ fontFamily: "Roboto", fontSize: "13px" }}
            type="number"
            domain={[0, maxPrice]}
            label={{
              value: "Price (₹)",
              angle: 90,
              position: "insideRight",
              fontFamily: "Roboto",
              fontSize: "14px",
              color: "#777777",
            }}
          />
          <Tooltip
            formatter={(value, name, props) => {
              if (
                typeof name === "string" &&
                name.toLowerCase().includes("occupancy")
              ) {
                return [`${value.toFixed(1)}%`, name];
              }
              const dataKey = props?.dataKey;
              const rawKey = dataKey ? `${dataKey}Raw` : null;
              const rawValue = rawKey ? props?.payload?.[rawKey] : undefined;
              return [getPriceLabel(rawValue, value), name];
            }}
            wrapperStyle={{
              fontSize: "12px",
              fontFamily: "Roboto",
            }}
          />
          <Legend
            onClick={handleLegendClick}
            formatter={(value, entry, index) => (
              <span
                style={{
                  fontSize: "14px",
                  fontFamily: "Roboto",
                  cursor: "pointer",
                }}
              >
                {value}
              </span>
            )}
          />

          <Bar
            yAxisId="left"
            dataKey="occupancy"
            name="Occupancy %"
            barSize={40}
            fill={
              opacity["occupancy"] === 1 ? "#AAAAAA" : "rgba(220, 220, 220)"
            }
            opacity={opacity["occupancy"] === 1 ? 1 : 0}
            onMouseOver={handleColorChange}
            onMouseOut={handleColorReset}
          >
            {chartData.map((entry, index) => (
              <Cell
                cursor="pointer"
                fill={index === activeIndex ? "#306FBC" : "#EBEBEB"}
                key={`cell-${index}`}
              />
            ))}
          </Bar>

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="systemPrice"
            name={`${currentHotel.name} (Recommended Price)`}
            stroke={
              opacity["systemPrice"] === 1 ? "#FF6B6B" : "rgba(220, 220, 220)"
            }
            strokeWidth={2}
            dot={{ r: 4 }}
            connectNulls
            strokeOpacity={opacity["systemPrice"]}
            activeDot={{ r: 6 }}
          />

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="currentPrice"
            name={`${currentHotel.name} (Current Price)`}
            stroke={
              opacity["currentPrice"] === 1 ? "#808080" : "rgba(220, 220, 220)"
            }
            strokeWidth={2}
            dot={{ r: 4 }}
            connectNulls
            strokeOpacity={opacity["currentPrice"]}
            activeDot={{ r: 6 }}
          />

          {competitorNames.map((compName, index) => (
            <Line
              key={compName}
              yAxisId="right"
              type="monotone"
              dataKey={compName}
              name={compName}
              stroke={
                opacity[compName] === 1
                  ? colorPalette[index % colorPalette.length]
                  : "rgba(220, 220, 220)"
              }
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              strokeOpacity={opacity[compName]}
              activeDot={{ r: 5 }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default LeadTimePricingChart;
